import React, { useMemo, useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAppTheme } from '../../contexts/ThemeContext';
import Icon from '../Icon';
import { isAdmin } from '../../utils/permissions.js';

const ADMIN_ONLY_SECTIONS = new Set(['administration', 'development']);

const IconMenu = ({ activeSection, onSectionChange, user, onAccountClick, onSettingsClick, onLogout }) => {
  const { theme } = useAppTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const menuItems = useMemo(() => {
    const allItems = [
      {
        id: 'ads-manager',
        icon: 'hub',
        label: 'Ads Manager',
        color: theme.colors.info,
      },
      {
        id: 'ad-manager',
        icon: 'campaign',
        label: 'Advertisers',
        color: theme.colors.primary,
      },
      {
        id: 'agent-studio',
        icon: 'flow',
        label: 'Agent Studio',
        color: theme.colors.info,
      },
      {
        id: 'administration',
        icon: 'admin',
        label: 'Administration',
        color: theme.colors.info,
      },
      {
        id: 'development',
        icon: 'code',
        label: 'Development',
        color: theme.colors.success,
      },
    ];

    if (!isAdmin(user)) {
      return allItems.filter((item) => !ADMIN_ONLY_SECTIONS.has(item.id));
    }
    return allItems;
  }, [theme, user]);

  const profileMenuItems = [
    {
      id: 'account',
      label: 'Account',
      icon: <PersonIcon fontSize="small" />,
      onClick: () => {
        setIsMenuOpen(false);
        onAccountClick?.();
      },
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon fontSize="small" />,
      onClick: () => {
        setIsMenuOpen(false);
        onSettingsClick?.();
      },
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: <LogoutIcon fontSize="small" />,
      onClick: () => {
        setIsMenuOpen(false);
        onLogout?.();
      },
    },
  ];

  const tooltipProps = {
    placement: 'right',
    arrow: true,
    enterDelay: 300,
    leaveDelay: 0,
    componentsProps: {
      tooltip: {
        sx: {
          backgroundColor: theme.colors.tooltip,
          color: theme.colors.text.inverse,
          fontSize: theme.fontSize.sm,
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          borderRadius: theme.borderRadius.sm,
        },
      },
      arrow: {
        sx: {
          color: theme.colors.tooltip,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '20px',
        flexGrow: 1,
      }}
    >
      {menuItems.map((item) => {
        const isActive = activeSection === item.id;
        return (
          <Tooltip key={item.id} title={item.label} {...tooltipProps}>
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                onClick={() => onSectionChange(item.id)}
                size="large"
                sx={{
                  width: theme.dimensions.buttonHeight,
                  height: theme.dimensions.buttonHeight,
                  borderRadius: theme.borderRadius.md,
                  backgroundColor: isActive ? theme.colors.primary : 'transparent',
                  color: isActive ? theme.colors.text.primary : theme.colors.text.secondary,
                  marginBottom: theme.spacing.md,
                  transition: theme.transitions.fast,
                  '& .MuiSvgIcon-root': {
                    fontSize: theme.dimensions.iconSize,
                    width: theme.dimensions.iconSize,
                    height: theme.dimensions.iconSize,
                  },
                  '&:hover': {
                    backgroundColor: isActive ? theme.colors.background.button : `${item.color}15`,
                    color: theme.colors.text.primary,
                    transform: 'scale(1.05)',
                    transition: theme.transitions.fast,
                  },
                  '&:active': {
                    backgroundColor: isActive ? theme.colors.background.button : theme.colors.background.iconMenu,
                  },
                  ...(isActive && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3,
                      height: theme.dimensions.buttonHeight,
                      backgroundColor: theme.colors.accent,
                      borderRadius: '2px 0 0 2px',
                    },
                  }),
                }}
              >
                <Icon name={item.icon} size="medium" />
              </IconButton>
            </span>
          </Tooltip>
        );
      })}

      {user && (
        <Box ref={menuRef} sx={{ marginTop: 'auto', paddingBottom: '20px', position: 'relative' }}>
          <IconButton
            onClick={() => setIsMenuOpen((prev) => !prev)}
            sx={{
              width: theme.dimensions.buttonHeight,
              height: theme.dimensions.buttonHeight,
              padding: 0,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: theme.transitions.fast,
              },
            }}
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                referrerPolicy="no-referrer"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: `2px solid ${isMenuOpen ? theme.colors.accent : theme.colors.border}`,
                  objectFit: 'cover',
                  transition: theme.transitions.fast,
                }}
              />
            ) : (
              <PersonIcon sx={{ fontSize: 28, color: theme.colors.text.secondary }} />
            )}
          </IconButton>

          {isMenuOpen && (
            <Box
              sx={{
                position: 'absolute',
                left: '100%',
                bottom: 0,
                marginLeft: '8px',
                backgroundColor: theme.colors.background.modal,
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.dropdown,
                border: `1px solid ${theme.colors.border}`,
                minWidth: '180px',
                zIndex: theme.zIndex.tooltip,
                overflow: 'hidden',
                animation: 'slideRight 0.15s ease-out',
                '@keyframes slideRight': {
                  from: { opacity: 0, transform: 'translateX(-8px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <Box sx={{ padding: '8px 12px', borderBottom: `1px solid ${theme.colors.border}` }}>
                <Box
                  sx={{
                    fontSize: theme.fontSize.md,
                    fontWeight: theme.fontWeight.semibold,
                    color: theme.colors.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.name}
                </Box>
                <Box
                  sx={{
                    fontSize: theme.fontSize.sm,
                    color: theme.colors.text.secondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.email}
                </Box>
              </Box>
              {profileMenuItems.map((item) => (
                <Box
                  key={item.id}
                  onClick={item.onClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    color: item.id === 'logout' ? theme.colors.danger : theme.colors.text.primary,
                    fontSize: theme.fontSize.md,
                    transition: theme.transitions.fast,
                    '&:hover': {
                      backgroundColor: theme.colors.background.sidebar,
                    },
                  }}
                >
                  {item.icon}
                  {item.label}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default IconMenu;
